import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import { ShoppingCart, RefreshCw, Loader2 } from "lucide-react";

type StripeCheckout = {
  id: string;
  stripeSessionId: string;
  userId: string;
  ownerId: string;
  ownerType: string;
  planId: string;
  checkoutType: string;
  existingSubscriptionId: string | null;
  status: string;
  stripeSubscriptionId: string | null;
  subscriptionId: string | null;
  completedAt: Date | null;
  error: string | null;
  createdAt: Date;
  expiresAt: Date;
  userName: string | null;
  userEmail: string | null;
  planName: string | null;
};

const formatDate = (date: Date | string | null) => {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString();
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "completed":
      return (
        <Badge variant="outline" className="text-neon-green border-neon-green">
          Completed
        </Badge>
      );
    case "pending":
      return <Badge variant="secondary">Pending</Badge>;
    case "expired":
      return (
        <Badge variant="outline" className="text-chromium-mist border-chromium-mist">
          Expired
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="outline" className="text-red-400 border-red-400">
          Failed
        </Badge>
      );
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export function StripeCheckouts() {
  const {
    data: checkoutsData,
    isLoading: checkoutsLoading,
    refetch: refetchCheckouts,
  } = trpc.admin.listStripeCheckouts.useQuery({ limit: 50 });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          Stripe Checkouts
        </CardTitle>
        <CardDescription>
          Checkout sessions created for subscriptions. Shows the intended owner and status.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {checkoutsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-chromium-mist" />
          </div>
        ) : !checkoutsData?.checkouts || checkoutsData.checkouts.length === 0 ? (
          <div className="text-center py-8 text-chromium-mist">
            <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No checkout sessions found</p>
            <p className="text-sm mt-2">
              Checkout records will appear here when users start subscription flows
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(checkoutsData.checkouts as StripeCheckout[]).map((checkout) => (
                <TableRow key={checkout.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-silica">
                        {checkout.userName || "Unknown"}
                      </p>
                      <p className="text-xs text-chromium-mist">
                        {checkout.userEmail || checkout.userId}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm text-silica">{checkout.ownerId}</p>
                      <p className="text-xs text-chromium-mist">{checkout.ownerType}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-silica">
                    {checkout.planName || checkout.planId}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{checkout.checkoutType}</Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(checkout.status)}</TableCell>
                  <TableCell className="text-sm text-chromium-mist">
                    {formatDate(checkout.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <div className="mt-4 pt-4 border-t border-slate-core">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchCheckouts()}
            disabled={checkoutsLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${checkoutsLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
